import React, { useState, useEffect, useMemo } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { RefreshCcw, CircleX } from "lucide-react";

import { getTenantIdFromHost } from "../Tenant/getTenantId";
import { fetchForms } from "../utils/forms";
import "../Dashboard.css";

export default function Dashboard() {
  const tenantId = getTenantIdFromHost();

  const [invites, setInvites] = useState([]);

  const [selectedSurveyId, setSelectedSurveyId] = useState("");
  const [forms, setForms] = useState([]);
  const [formFilter, setFormFilter] = useState("visible");

  const [sortConfig, setSortConfig] = React.useState({
    key: "customerName",
    direction: "asc",
  });

  useEffect(() => {
    const fetchInvites = async () => {
      if (!selectedSurveyId) {
        setInvites([]);
        return;
      }

      const qRef = query(
        collection(db, "tenants", tenantId, "surveyInvites"),
        where("surveyId", "==", selectedSurveyId),
      );

      const snapshot = await getDocs(qRef);

      const allInvites = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setInvites(allInvites);
    };
    // console.log(invites);
    fetchInvites();
  }, [selectedSurveyId, tenantId]);

  useEffect(() => {
    const load = async () => {
      const data = await fetchForms(formFilter);
      setForms(data);
    };
    load();
  }, [formFilter]);

  const handleSurveyChange = (id) => {
    setSelectedSurveyId(id);
  };

  function getSendCount(invite) {
    return (invite.smsSentCount || 0) + (invite.emailSentCount || 0);
  }

  function getReminderStats(invites) {
    const groups = {
      "1 utskick": [],
      "2 utskick": [],
      "3+ utskick": [],
    };

    invites.forEach((invite) => {
      const count = getSendCount(invite);

      if (count <= 1) groups["1 utskick"].push(invite);
      else if (count === 2) groups["2 utskick"].push(invite);
      else groups["3+ utskick"].push(invite);
    });

    return Object.entries(groups).map(([label, items]) => {
      const submitted = items.filter((i) => i.status === "submitted").length;

      return {
        label,
        total: items.length,
        submitted,
        rate: getPercent(submitted, items.length),
      };
    });
  }

  function getWeekdayStats(invites) {
    const weekdays = [
      "Söndag",
      "Måndag",
      "Tisdag",
      "Onsdag",
      "Torsdag",
      "Fredag",
      "Lördag",
    ];

    const groups = {};

    weekdays.forEach((day) => {
      groups[day] = [];
    });

    invites.forEach((invite) => {
      const lastContact = getLastContactMillis(invite);
      if (!lastContact) return;

      const dayName = weekdays[new Date(lastContact).getDay()];
      groups[dayName].push(invite);
    });

    return weekdays.map((day) => {
      const items = groups[day];
      const submitted = items.filter((i) => i.status === "submitted").length;

      return {
        day,
        total: items.length,
        submitted,
        rate: getPercent(submitted, items.length),
      };
    });
  }

  function getLastContactMillis(invite) {
    const sms = toMillis(invite.lastSmsSentAt);
    const email = toMillis(invite.lastEmailSentAt);

    if (!sms && !email) return null;
    if (sms && (!email || sms > email)) return sms;

    return email;
  }

  function getLastContactLabel(invite) {
    const sms = toMillis(invite.lastSmsSentAt);
    const email = toMillis(invite.lastEmailSentAt);

    if (!sms && !email) return "Ingen";

    if (sms && (!email || sms > email)) {
      return `SMS ${formatDate(invite.lastSmsSentAt)}`;
    }

    return `E-post ${formatDate(invite.lastEmailSentAt)}`;
  }

  function formatDate(value) {
    const millis = toMillis(value);
    if (!millis) return "";

    return new Date(millis).toLocaleDateString("sv-SE", {
      month: "numeric",
      day: "numeric",
    });
  }

  function getSortValue(invite, key) {
    switch (key) {
      case "customerName":
        return invite.customerName || "";

      case "customerId":
        return invite.customerId || "";

      case "officeId":
        return invite.officeId || "";

      case "employeeId":
        return invite.employeeId || "";

      case "statusLabel":
        return invite.opened || invite.firstOpenedAt
          ? "Öppnad ej besvarad"
          : "Ej öppnad";

      case "smsSentCount":
        return invite.smsSentCount || 0;

      case "emailSentCount":
        return invite.emailSentCount || 0;

      case "lastContact":
        return getLastContactMillis(invite) || 0;

      case "suggestion":
        return getFollowUpSuggestion(invite) || "";

      default:
        return invite[key] || "";
    }
  }

  function sortInvites(invites, sortConfig) {
    return [...invites].sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      return sortConfig.direction === "asc"
        ? String(aValue).localeCompare(String(bValue), "sv")
        : String(bValue).localeCompare(String(aValue), "sv");
    });
  }

  //   const stats = useMemo(() => {
  //     const total = invites.length;

  //     const submitted = invites.filter((i) => i.status === "submitted");
  //     const opened = invites.filter((i) => i.opened || i.firstOpenedAt);
  //     const openButNotSubmitted = invites.filter(
  //       (i) => (i.opened || i.firstOpenedAt) && i.status !== "submitted",
  //     );

  //     const withSms = invites.filter((i) => i.smsSentCount > 0);
  //     const withEmail = invites.filter((i) => i.emailSentCount > 0);

  //     const submittedAfterSms = submitted.filter(
  //       (i) => getLikelyTrigger(i) === "sms",
  //     );

  //     const submittedAfterEmail = submitted.filter(
  //       (i) => getLikelyTrigger(i) === "email",
  //     );

  //     return {
  //       total,
  //       submitted: submitted.length,
  //       opened: opened.length,
  //       openButNotSubmitted: openButNotSubmitted.length,

  //       withSms: withSms.length,
  //       withEmail: withEmail.length,

  //       submittedAfterSms: submittedAfterSms.length,
  //       submittedAfterEmail: submittedAfterEmail.length,

  //       responseRate: getPercent(submitted.length, total),
  //       openRate: getPercent(opened.length, total),
  //       submitAfterOpenRate: getPercent(submitted.length, opened.length),
  //     };
  //   }, [invites]);

  const stats = useMemo(() => {
    const total = invites.length;

    const submitted = invites.filter((i) => i.status === "submitted");
    const opened = invites.filter((i) => i.opened || i.firstOpenedAt);
    const openButNotSubmitted = invites.filter(
      (i) => (i.opened || i.firstOpenedAt) && i.status !== "submitted",
    );

    const withSms = invites.filter((i) => i.smsSentCount > 0);
    const withEmail = invites.filter((i) => i.emailSentCount > 0);

    const submittedAfterSms = submitted.filter(
      (i) => getLikelyTrigger(i) === "sms",
    );

    const submittedAfterEmail = submitted.filter(
      (i) => getLikelyTrigger(i) === "email",
    );

    const withReminders = invites.filter((i) => getSendCount(i) > 1);
    const submittedWithReminders = withReminders.filter(
      (i) => i.status === "submitted",
    );

    return {
      total,
      submitted: submitted.length,
      opened: opened.length,
      openButNotSubmitted: openButNotSubmitted.length,

      withSms: withSms.length,
      withEmail: withEmail.length,

      submittedAfterSms: submittedAfterSms.length,
      submittedAfterEmail: submittedAfterEmail.length,

      smsResponseRate: getPercent(submittedAfterSms.length, withSms.length),
      emailResponseRate: getPercent(
        submittedAfterEmail.length,
        withEmail.length,
      ),

      withReminders: withReminders.length,
      submittedWithReminders: submittedWithReminders.length,
      reminderResponseRate: getPercent(
        submittedWithReminders.length,
        withReminders.length,
      ),

      responseRate: getPercent(submitted.length, total),
      openRate: getPercent(opened.length, total),
      submitAfterOpenRate: getPercent(submitted.length, opened.length),

      weekdayStats: getWeekdayStats(invites),
      reminderStats: getReminderStats(invites),
    };
  }, [invites]);
  const openInvites = invites.filter((i) => i.status !== "submitted");

  const handleSort = (key) => {
    setSortConfig((current) => {
      if (current.key === key) {
        return {
          key,
          direction: current.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        key,
        direction: "asc",
      };
    });
  };

  function SortHeader({ label, sortKey, sortConfig, onSort }) {
    const isActive = sortConfig.key === sortKey;
    const arrow = isActive
      ? sortConfig.direction === "asc"
        ? " ▲"
        : " ▼"
      : "";

    return (
      <button
        type="button"
        className="sort-header"
        onClick={() => onSort(sortKey)}
      >
        {label}
        {arrow}
      </button>
    );
  }

  const sortedOpenInvites = useMemo(() => {
    return sortInvites(openInvites, sortConfig);
  }, [openInvites, sortConfig]);

  return (
    <>
      <div className="visualizeWrapper" style={{ marginTop: 50 }}>
        <div className="surveyPicker">
          <div className="horizontalWrapper">
            <h3>
              <label>Välj enkät:</label>{" "}
            </h3>
            {/* {selectedSurveyId ? (
              <>
                <button
                  type="button"
                  className="plainButton"
                  onClick={() => handleSurveyChange("")}
                >
                  <CircleX size={36} strokeWidth={2} color="#333" />
                </button>
              </>
            ) : null} */}
          </div>
          <div className="surveyPills">
            {forms.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`surveyPill ${selectedSurveyId === f.id ? "active" : ""}`}
                onClick={() => handleSurveyChange(f.id)}
              >
                {f.formName}
              </button>
            ))}

            {selectedSurveyId ? (
              <>
                <button
                  type="button"
                  className={"iconBtnL"}
                  onClick={() => handleSurveyChange(selectedSurveyId, true)}
                >
                  <RefreshCcw size={36} strokeWidth={2} color="#333" />
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>
{selectedSurveyId ? (
      <div className="visualizeWrapper" style={{ marginTop: 50 }}>
        <h2>Svarsfrekvens & utskick</h2>
        <p className="page-intro">
          Den här vyn visar hur enkäterna följs upp: vilka som har fått SMS
          eller e-post, vilka som har öppnat länken och vilka som har svarat.
        </p>

        <section className="stats-grid">
          <StatCard title="Skickade enkäter" value={stats.total} />
          <StatCard title="Besvarade" value={stats.submitted} />
          <StatCard title="Svarsfrekvens" value={`${stats.responseRate}%`} />
          <StatCard title="Öppnade länkar" value={stats.opened} />
          <StatCard title="Öppningsgrad" value={`${stats.openRate}%`} />
          <StatCard
            title="Svar efter öppning"
            value={`${stats.submitAfterOpenRate}%`}
          />
        </section>

        <section className="channel-summary">
          <h3>Kontaktvägar</h3>
          <p className="page-intro">
            “Svar efter SMS/e-post” baseras på den senaste registrerade
            kontakten före att kunden skickade in sitt svar.
          </p>

          <div
            className="stats-grid small"
            style={{ display: "flex", flexDirection: "column" }}
          >
            <div
              style={{ display: "flex", flexDirection: "row", width: "100%" }}
            >
              {" "}
              <StatCard title="Har fått SMS" value={stats.withSms} />
              <StatCard
                title="Svar efter SMS"
                value={stats.submittedAfterSms}
              />
              <StatCard
                title="Svarsfrekvens SMS"
                value={`${stats.smsResponseRate}%`}
              />
            </div>
            <div
              style={{ display: "flex", flexDirection: "row", width: "100%" }}
            >
              {" "}
              <StatCard title="Har fått e-post" value={stats.withEmail} />
              <StatCard
                title="Svar efter e-post"
                value={stats.submittedAfterEmail}
              />
              <StatCard
                title="Svarsfrekvens e-post"
                value={`${stats.emailResponseRate}%`}
              />
            </div>
          </div>
        </section>

        <section className="reminder-summary">
          <h3>Påminnelser</h3>

          <div className="mini-table">
            <div className="mini-row mini-header">
              <span>Antal utskick</span>
              <span>Totalt</span>
              <span>Besvarade</span>
              <span>Svarsfrekvens</span>
            </div>

            {stats.reminderStats.map((row) => (
              <div className="mini-row" key={row.label}>
                <span>{row.label}</span>
                <span>{row.total}</span>
                <span>{row.submitted}</span>
                <span>{row.rate}%</span>
              </div>
            ))}
          </div>
        </section>

        <section className="weekday-summary">
          <h3>Veckodagar</h3>
          <p className="section-help">
            Räknat på senaste kontakt före svar eller uppföljning.
          </p>

          <div className="mini-table">
            <div className="mini-row mini-header">
              <span>Dag</span>
              <span>Skickade</span>
              <span>Besvarade</span>
              <span>Svarsfrekvens</span>
            </div>

            {stats.weekdayStats.map((row) => (
              <div className="mini-row" key={row.day}>
                <span>{row.day}</span>
                <span>{row.total}</span>
                <span>{row.submitted}</span>
                <span>{row.rate}%</span>
              </div>
            ))}
          </div>
        </section>

        <section className="followup-list">
          <h3>Obesvarade</h3>

          {openInvites.length === 0 ? (
            <p>Alla inbjudningar är besvarade.</p>
          ) : (
            <div className="invite-table">
              <div className="invite-row invite-header">
                <SortHeader
                  label="Kund"
                  sortKey="customerName"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Kontor"
                  sortKey="officeId"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Handläggare"
                  sortKey="employeeId"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Status"
                  sortKey="statusLabel"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <SortHeader
                  label="SMS"
                  sortKey="smsSentCount"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <SortHeader
                  label="E-post"
                  sortKey="emailSentCount"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Senaste kontakt"
                  sortKey="lastContact"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Tolkning"
                  sortKey="suggestion"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </div>

              {sortedOpenInvites.map((invite) => (
                <div className="invite-row" key={invite.id}>
                  <span>
                    <strong>{invite.customerName}</strong>
                    <br />
                    <small>{invite.customerId}</small>
                  </span>

                  <span>{invite.officeId || "-"}</span>
                  <span>{invite.employeeId || "-"}</span>

                  <span>
                    {invite.opened || invite.firstOpenedAt
                      ? "Öppnad ej besvarad"
                      : "Ej öppnad"}
                  </span>

                  <span>{invite.smsSentCount || 0}</span>
                  <span>{invite.emailSentCount || 0}</span>

                  <span>{getLastContactLabel(invite)}</span>

                  <span>{getFollowUpSuggestion(invite)}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
) : null}
    </>
  );
}

function StatCard({ title, value }) {
  return (
    <div className="stat-card">
      <div className="stat-value">{value}</div>
      <div className="stat-title">{title}</div>
    </div>
  );
}

function getPercent(part, total) {
  if (!total) return 0;
  return Math.round((part / total) * 100);
}

function toMillis(value) {
  if (!value) return null;

  // Firestore Timestamp
  if (typeof value.toMillis === "function") {
    return value.toMillis();
  }

  // Date eller string
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
}

function getLikelyTrigger(invite) {
  const submittedAt = toMillis(invite.submittedAt);
  const openedAt = toMillis(invite.firstOpenedAt);

  const target = submittedAt || openedAt;
  if (!target) return null;

  const contacts = [
    {
      channel: "email",
      sentAt: toMillis(invite.lastEmailSentAt),
    },
    {
      channel: "sms",
      sentAt: toMillis(invite.lastSmsSentAt),
    },
  ].filter((c) => c.sentAt && c.sentAt <= target);

  if (contacts.length === 0) return null;

  contacts.sort((a, b) => b.sentAt - a.sentAt);

  return contacts[0].channel;
}

function getLastContactLabel(invite) {
  const sms = toMillis(invite.lastSmsSentAt);
  const email = toMillis(invite.lastEmailSentAt);

  if (!sms && !email) return "Ingen kontakt registrerad";

  if (sms && (!email || sms > email)) {
    return "SMS";
  }

  return "E-post";
}

function getFollowUpSuggestion(invite) {
  if (invite.status === "submitted") {
    const trigger = getLikelyTrigger(invite);

    if (trigger === "sms") return "Svarade efter SMS";
    if (trigger === "email") return "Svarade efter e-post";

    return "Besvarad";
  }

  if (invite.opened || invite.firstOpenedAt) {
    return "Har öppnat – kan vara värd personlig uppföljning";
  }

  if (invite.emailSentCount > 0 && !invite.smsSentCount) {
    return "Har bara fått e-post – testa SMS";
  }

  if (invite.smsSentCount > 0 && !invite.emailSentCount) {
    return "Har bara fått SMS";
  }

  if (invite.smsSentCount > 0 && invite.emailSentCount > 0) {
    return "Har fått både SMS och e-post";
  }

  return "Ingen åtgärd registrerad";
}
